import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, DatePicker, InputNumber, Select, Table, Tabs, Tag, Tooltip, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { hasResource } from 'utils/resourceAuth';
import { ResourceCode } from 'constants/resourceCodes';
import { getToken } from 'utils/auth';
import { WEBSITE_CONTENT_OPERATION_LOGS } from '../graphql/queries';
import type {
    WebsiteContentLang,
    WebsiteLogObjectType,
    WebsiteLogOperationType,
    WebsiteOperationLog,
    WebsiteOperationLogList,
} from '../graphql/types';
import { resolveWebsiteContentError } from '../utils/errorMessage';
import { formatLocalDateTime } from '../utils/format';

const PAGE_SIZE = 20;
/** 默认看最近 30 天 */
const DEFAULT_RANGE_DAYS = 30;
const OPERATIONS: Record<WebsiteLogObjectType, WebsiteLogOperationType[]> = {
    CONTENT: ['CREATE', 'UPDATE', 'PUBLISH', 'SCHEDULE', 'CANCEL_SCHEDULE', 'UNPUBLISH', 'DELETE'],
    MEDIA: ['UPLOAD', 'DELETE'],
};
const OPERATION_COLORS: Partial<Record<WebsiteLogOperationType, string>> = {
    PUBLISH: 'green',
    SCHEDULE: 'blue',
    UNPUBLISH: 'orange',
    DELETE: 'red',
    UPLOAD: 'purple',
};

const uiLang = (language: string): WebsiteContentLang => {
    const code = language.toLowerCase().split('-')[0];
    return code === 'zh' ? 'ZH' : code === 'es' ? 'ES' : 'EN';
};

/** 导出接口：REST 流式返回 CSV 文件，导出行数与是否截断在响应头 */
const EXPORT_URL = '/api/website-content/logs/export';
const EXPORT_ROW_COUNT_HEADER = 'X-Export-Row-Count';
const EXPORT_TRUNCATED_HEADER = 'X-Export-Truncated';

/** 文件名取响应头 Content-Disposition，取不到时用 [fallback] */
const exportFileName = (response: Response, fallback: string): string =>
    /filename="([^"]+)"/.exec(response.headers.get('Content-Disposition') ?? '')?.[1] ?? fallback;

/** 浏览器保存下载到的文件 */
const saveFile = (fileName: string, file: Blob) => {
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

/**
 * CMS 操作日志（T-A16）：内容 / 媒体两个页签，按操作类型、时间范围筛选；能看全部日志的人可按操作人筛选，
 * 其他人只看本人的操作。导出按当前筛选条件生成 CSV（最多 5 万行）。
 */
const WebsiteContentLogPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const objectType: WebsiteLogObjectType = searchParams.get('objectType') === 'MEDIA' ? 'MEDIA' : 'CONTENT';
    const operationParam = searchParams.get('operationType') as WebsiteLogOperationType | null;
    const operationType = operationParam && OPERATIONS[objectType].includes(operationParam) ? operationParam : null;
    const operatorParam = Number.parseInt(searchParams.get('operatorId') ?? '', 10);
    const operatorId = Number.isFinite(operatorParam) && operatorParam > 0 ? operatorParam : null;
    const page = Math.max(1, Number.parseInt(searchParams.get('page') ?? '', 10) || 1);
    const [range, setRange] = useState<[Dayjs, Dayjs]>(() => [
        dayjs().subtract(DEFAULT_RANGE_DAYS, 'day').startOf('day'),
        dayjs().endOf('day'),
    ]);
    const [exporting, setExporting] = useState(false);
    const canViewAll = hasResource([ResourceCode.websiteContent.LOG_ALL]);

    const variables = useMemo(() => ({
        objectType,
        operationType,
        operatorId: canViewAll ? operatorId : null,
        startTime: range[0].toISOString(),
        endTime: range[1].toISOString(),
    }), [objectType, operationType, operatorId, canViewAll, range]);

    const { data, previousData, loading, error } = useQuery<{ websiteContentOperationLogs: WebsiteOperationLogList }>(
        WEBSITE_CONTENT_OPERATION_LOGS,
        { variables: { ...variables, page, pageSize: PAGE_SIZE }, fetchPolicy: 'cache-and-network' },
    );
    const list = (data ?? previousData)?.websiteContentOperationLogs;

    useEffect(() => {
        if (!error) return;
        const text = resolveWebsiteContentError(error, t);
        if (text) message.error(text);
    }, [error, t]);

    const updateParams = (patch: Record<string, string | null>) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(patch).forEach(([key, value]) => {
            if (value) next.set(key, value);
            else next.delete(key);
        });
        if (!('page' in patch)) next.delete('page');
        setSearchParams(next);
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const params = new URLSearchParams({
                objectType: variables.objectType,
                startTime: variables.startTime,
                endTime: variables.endTime,
                lang: uiLang(i18n.language),
            });
            if (variables.operationType) params.set('operationType', variables.operationType);
            if (variables.operatorId) params.set('operatorId', String(variables.operatorId));
            const token = getToken();
            const response = await fetch(`${EXPORT_URL}?${params.toString()}`, {
                headers: { authorization: token ? `Bearer ${token}` : '' },
            });
            if (!response.ok) throw new Error(`export failed: HTTP ${response.status}`);
            saveFile(exportFileName(response, `website-${objectType.toLowerCase()}-logs.csv`), await response.blob());
            const count = Number(response.headers.get(EXPORT_ROW_COUNT_HEADER) ?? 0);
            if (response.headers.get(EXPORT_TRUNCATED_HEADER) === 'true') {
                message.warning(t('websiteContent.logs.exportTruncated', { count }));
            } else {
                message.success(t('websiteContent.logs.exported', { count }));
            }
        } catch {
            message.error(t('websiteContent.logs.exportFailed'));
        } finally {
            setExporting(false);
        }
    };

    const columns: ColumnsType<WebsiteOperationLog> = [
        { title: t('websiteContent.logs.columns.time'), key: 'time', width: 170, render: (_, log) => formatLocalDateTime(log.operatedAt) },
        {
            title: t('websiteContent.logs.columns.operator'),
            key: 'operator',
            width: 200,
            render: (_, log) => (log.operatorId
                ? log.operatorName ?? log.operatorId
                : (
                    <Tooltip title={log.scheduledBy ? t('websiteContent.logs.scheduledBy', { id: log.scheduledBy }) : undefined}>
                        <span className="text-gray-500">{t('websiteContent.list.systemOperator')}</span>
                    </Tooltip>
                )),
        },
        {
            title: t('websiteContent.logs.columns.operation'),
            key: 'operation',
            width: 120,
            render: (_, log) => (
                <Tag color={OPERATION_COLORS[log.operationType]}>{t(`websiteContent.logs.operations.${log.operationType}`)}</Tag>
            ),
        },
        {
            title: t('websiteContent.logs.columns.object'),
            key: 'object',
            render: (_, log) => (
                <div>
                    <div className="break-all">{log.objectName || <span className="text-gray-400">{t('websiteContent.list.untitled')}</span>}</div>
                    <div className="text-xs text-gray-400 font-mono">{log.slug ?? `#${log.objectId}`}</div>
                </div>
            ),
        },
        { title: 'IP', key: 'ip', width: 150, render: (_, log) => log.ip ?? '-' },
    ];

    return (
        <div className="flex flex-col gap-4 min-h-0">
            <div className="flex items-center justify-between">
                <Typography.Title level={4} className="!mb-0">{t('websiteContent.logs.title')}</Typography.Title>
                <Button icon={<DownloadOutlined />} loading={exporting} onClick={handleExport}>
                    {t('websiteContent.logs.export')}
                </Button>
            </div>
            {!canViewAll && <Alert type="info" showIcon message={t('websiteContent.logs.ownOnly')} />}
            <Tabs
                activeKey={objectType}
                onChange={(key) => updateParams({ objectType: key === 'MEDIA' ? 'MEDIA' : null, operationType: null })}
                items={[
                    { key: 'CONTENT', label: t('websiteContent.logs.objects.CONTENT') },
                    { key: 'MEDIA', label: t('websiteContent.logs.objects.MEDIA') },
                ]}
                className="!mb-0"
            />
            <div className="flex flex-wrap items-center gap-3">
                <Select
                    className="w-44"
                    allowClear
                    placeholder={t('websiteContent.logs.allOperations')}
                    value={operationType ?? undefined}
                    onChange={(value) => updateParams({ operationType: value ?? null })}
                    options={OPERATIONS[objectType].map((operation) => ({
                        value: operation,
                        label: t(`websiteContent.logs.operations.${operation}`),
                    }))}
                />
                <DatePicker.RangePicker
                    allowClear={false}
                    value={range}
                    onChange={(value) => {
                        if (value?.[0] && value?.[1]) {
                            setRange([value[0].startOf('day'), value[1].endOf('day')]);
                            updateParams({ page: null });
                        }
                    }}
                />
                {canViewAll && (
                    <InputNumber
                        className="!w-48"
                        min={1}
                        precision={0}
                        placeholder={String(t('websiteContent.logs.operatorIdPlaceholder'))}
                        value={operatorId}
                        onChange={(value) => updateParams({ operatorId: value ? String(value) : null })}
                    />
                )}
            </div>
            <Table<WebsiteOperationLog>
                rowKey="id"
                columns={columns}
                dataSource={list?.items ?? []}
                loading={loading}
                locale={{ emptyText: t('websiteContent.logs.empty') }}
                expandable={{
                    expandedRowRender: (log) => <span className="text-gray-500">{log.description}</span>,
                    rowExpandable: (log) => Boolean(log.description),
                }}
                pagination={{
                    current: page,
                    pageSize: PAGE_SIZE,
                    total: list?.total ?? 0,
                    showSizeChanger: false,
                    showTotal: (total) => t('websiteContent.list.total', { total }),
                    onChange: (next) => updateParams({ page: next > 1 ? String(next) : null }),
                }}
            />
        </div>
    );
};

export default WebsiteContentLogPage;
